/**
 * KizunaAI region guide: where audio is processed.
 *
 * Screenshots were taken from the Sokuji v0.41.1 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'Choose Where Your Audio Is Processed',
  backLink: 'Back to KizunaAI Quick Start',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Overview',
    content:
      'Each KizunaAI engine runs a separate deployment per region, and your audio is processed in the region you pick. The Soniox engine offers the United States, the European Union and Japan; the Qwen engine offers Singapore and Beijing. The defaults are the United States and Singapore.',
  },
  steps: [
    {
      title: 'Open the region setting',
      content:
        'Open <strong>Settings</strong>, switch to <strong>Advanced</strong> with the button at the top right of the panel, and choose the <strong>Provider</strong> tab. <strong>Region</strong> is right below the AI service provider.',
      screenshot: '01-region.png',
    },
    {
      title: 'Pick a region',
      content:
        'With the Soniox engine, choose <strong>United States</strong>, <strong>European Union</strong> or <strong>日本</strong> (Japan); with the Qwen engine, <strong>Singapore</strong> or <strong>北京</strong> (Beijing), for example the region your organization requires for its data. The choice applies from the next session.',
      screenshot: '02-region-qwen.png',
    },
    {
      title: 'Add your voice in the new region',
      content:
        'With the Soniox engine, custom voices belong to one region, and each region remembers its own voice choice. If you use <strong>My voice</strong>, add it again after switching; see <a href="/docs/kizuna-ai/voices">Choose a voice or clone your own</a>. The Qwen engine’s voices are not tied to a region: its presets and live cloning work the same in Singapore and Beijing.',
    },
  ],
  tips: {
    title: 'Tips',
    items: [
      'The region cannot be changed while a session is starting or running. Stop the session first.',
      'Everything else, including languages, vocabulary and the session background, stays the same when you switch region.',
      'Beijing may answer with noticeably higher latency from networks outside mainland China; if it does, try Singapore.',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Which region should I choose?',
        answer:
          'If your organization has rules about where data is processed, pick the region they require. Otherwise the default works for everyone: the United States for Soniox, Singapore for Qwen. Beijing is there for networks in mainland China.',
      },
      {
        question: 'Where is my audio processed if I never change this?',
        answer: 'In the engine’s default region: the United States for Soniox, Singapore for Qwen.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Still stuck? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. You can also ask in <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: '选择音频的处理区域',
  backLink: '返回 KizunaAI 快速入门',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概述',
    content:
      'KizunaAI 的每个引擎在每个区域都有独立的部署，你的音频会在你选择的区域处理。Soniox 引擎提供美国、欧盟和日本，Qwen 引擎提供新加坡和北京。默认区域分别是美国和新加坡。',
  },
  steps: [
    {
      title: '打开区域设置',
      content:
        '打开<strong>设置</strong>，用面板右上角的按钮切换到<strong>高级</strong>，然后选择<strong>提供商</strong>标签页。<strong>区域</strong>就在 AI 服务提供商的正下方。',
      screenshot: '01-region.png',
    },
    {
      title: '选择区域',
      content:
        '选择一个区域，例如你所在组织对其数据所要求的区域：使用 Soniox 引擎时是 <strong>United States</strong>、<strong>European Union</strong> 或 <strong>日本</strong>，使用 Qwen 引擎时是 <strong>Singapore</strong> 或 <strong>北京</strong>。该选择从下一次会话开始生效。',
      screenshot: '02-region-qwen.png',
    },
    {
      title: '在新区域添加你的声音',
      content:
        '使用 Soniox 引擎时，自定义声音只属于一个区域，每个区域会分别记住自己的声音选择。如果你使用<strong>我的声音</strong>，切换后请重新添加；参见<a href="/docs/kizuna-ai/voices">选择声音或克隆你自己的声音</a>。Qwen 引擎的声音不绑定区域：它的预设声音和实时克隆在新加坡和北京的效果相同。',
    },
  ],
  tips: {
    title: '提示',
    items: [
      '会话正在启动或运行时，无法更改区域。请先停止会话。',
      '切换区域时，其他所有设置（包括语言、词表和会话背景）都保持不变。',
      '从中国大陆以外的网络连接北京时，延迟可能明显偏高；如遇到，可改用新加坡。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '我应该选择哪个区域？',
        answer:
          '如果你的组织对数据的处理地点有规定，请选择其要求的区域。否则，默认区域适合所有人：Soniox 用美国，Qwen 用新加坡。北京是为中国大陆的网络准备的。',
      },
      {
        question: '如果我从不更改这项设置，音频会在哪里处理？',
        answer: '在该引擎的默认区域：Soniox 是美国，Qwen 是新加坡。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content:
      '仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。你也可以在 <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> 中提问。',
  },
};

const ja: TutorialData = {
  pageTitle: '音声を処理する場所を選ぶ',
  backLink: 'KizunaAI クイックスタートに戻る',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概要',
    content:
      'KizunaAI の各エンジンは、リージョンごとに独立したデプロイを運用しており、音声は選んだリージョンで処理されます。Soniox エンジンは米国、欧州連合（EU）、日本、Qwen エンジンはシンガポールと北京から選べます。既定はそれぞれ米国とシンガポールです。',
  },
  steps: [
    {
      title: 'リージョン設定を開く',
      content:
        '<strong>設定</strong>を開き、パネル右上のボタンで<strong>詳細</strong>に切り替えて、<strong>プロバイダー</strong>タブを選びます。<strong>リージョン</strong>は AI サービスプロバイダーのすぐ下にあります。',
      screenshot: '01-region.png',
    },
    {
      title: 'リージョンを選ぶ',
      content:
        'Soniox エンジンでは<strong>United States</strong>、<strong>European Union</strong>、<strong>日本</strong>のいずれか、Qwen エンジンでは<strong>Singapore</strong>か<strong>北京</strong>を、たとえば組織がデータについて求めるリージョンに合わせて選びます。選択は次のセッションから適用されます。',
      screenshot: '02-region-qwen.png',
    },
    {
      title: '新しいリージョンで声を追加する',
      content:
        'Soniox エンジンでは、カスタム音声は 1 つのリージョンに属し、音声の選択もリージョンごとに記憶されます。<strong>マイ音声</strong>を使っている場合は、切り替えたあとにもう一度追加してください。詳しくは<a href="/docs/kizuna-ai/voices">音声を選ぶ・自分の声を複製する</a>を参照してください。Qwen エンジンの音声はリージョンに縛られません。プリセットもその場での複製も、シンガポールと北京で同じように使えます。',
    },
  ],
  tips: {
    title: 'ヒント',
    items: [
      'セッションの開始中や実行中は、リージョンを変更できません。先にセッションを停止してください。',
      'リージョンを切り替えても、言語、語彙、セッションの背景など、ほかの設定はすべてそのままです。',
      '中国本土以外のネットワークから北京に接続すると、遅延が大きくなることがあります。その場合はシンガポールをお試しください。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: 'どのリージョンを選べばよいですか？',
        answer:
          '組織にデータの処理場所に関する規定がある場合は、その規定が求めるリージョンを選んでください。そうでなければ、既定のリージョン（Soniox は米国、Qwen はシンガポール）で誰でも問題なく使えます。北京は、中国本土のネットワークで使う人のためのリージョンです。',
      },
      {
        question: 'この設定を一度も変えない場合、音声はどこで処理されますか？',
        answer: 'エンジンの既定のリージョンで処理されます。Soniox は米国、Qwen はシンガポールです。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content:
      'それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。<a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> で質問することもできます。',
  },
};

const ko: TutorialData = {
  pageTitle: '오디오 처리 지역 선택',
  backLink: 'KizunaAI 빠른 시작으로 돌아가기',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '개요',
    content:
      'KizunaAI의 각 엔진은 지역마다 별도의 배포를 운영하며, 오디오는 선택한 지역에서 처리됩니다. Soniox 엔진은 미국, 유럽 연합, 일본을, Qwen 엔진은 싱가포르와 베이징을 제공합니다. 기본값은 각각 미국과 싱가포르입니다.',
  },
  steps: [
    {
      title: '지역 설정 열기',
      content:
        '<strong>설정</strong>을 열고 패널 오른쪽 위의 버튼으로 <strong>고급</strong>으로 전환한 다음 <strong>제공자</strong> 탭을 선택하세요. <strong>지역</strong>은 AI 서비스 제공자 바로 아래에 있습니다.',
      screenshot: '01-region.png',
    },
    {
      title: '지역 선택',
      content:
        'Soniox 엔진에서는 <strong>United States</strong>, <strong>European Union</strong> 또는 <strong>日本</strong>(일본)을, Qwen 엔진에서는 <strong>Singapore</strong> 또는 <strong>北京</strong>(베이징)을 선택하세요. 조직이 데이터에 대해 요구하는 지역 등에 맞춰 고르면 됩니다. 선택은 다음 세션부터 적용됩니다.',
      screenshot: '02-region-qwen.png',
    },
    {
      title: '새 지역에서 음성 추가',
      content:
        'Soniox 엔진에서는 사용자 정의 음성이 하나의 지역에 속하며, 지역마다 음성 선택을 따로 기억합니다. <strong>내 음성</strong>을 사용한다면 지역을 바꾼 뒤 다시 추가하세요. <a href="/docs/kizuna-ai/voices">음성 선택 또는 내 목소리 복제</a>를 참조하세요. Qwen 엔진의 음성은 지역에 묶여 있지 않습니다. 프리셋과 실시간 복제는 싱가포르와 베이징에서 똑같이 작동합니다.',
    },
  ],
  tips: {
    title: '팁',
    items: [
      '세션이 시작 중이거나 실행 중일 때는 지역을 바꿀 수 없습니다. 먼저 세션을 중지하세요.',
      '지역을 바꿔도 언어, 어휘, 세션 배경 등 나머지 설정은 모두 그대로 유지됩니다.',
      '중국 본토 외부 네트워크에서는 베이징의 지연이 눈에 띄게 커질 수 있습니다. 그런 경우 싱가포르를 사용해 보세요.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: '어느 지역을 선택해야 하나요?',
        answer:
          '조직에 데이터 처리 위치에 관한 규정이 있다면 그 규정이 요구하는 지역을 고르세요. 그렇지 않다면 기본값(Soniox는 미국, Qwen은 싱가포르)으로 누구나 문제없이 사용할 수 있습니다. 베이징은 중국 본토의 네트워크를 위한 지역입니다.',
      },
      {
        question: '이 설정을 한 번도 바꾸지 않으면 오디오는 어디서 처리되나요?',
        answer: '엔진의 기본 지역에서 처리됩니다. Soniox는 미국, Qwen은 싱가포르입니다.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content:
      '그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>에서 질문할 수도 있습니다.',
  },
};

const es: TutorialData = {
  pageTitle: 'Elige dónde se procesa tu audio',
  backLink: 'Volver al inicio rápido de KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Descripción general',
    content:
      'Cada motor de KizunaAI tiene un despliegue independiente por región, y tu audio se procesa en la región que elijas. El motor de Soniox ofrece Estados Unidos, la Unión Europea y Japón; el motor de Qwen ofrece Singapur y Pekín. Las predeterminadas son Estados Unidos y Singapur.',
  },
  steps: [
    {
      title: 'Abre el ajuste de región',
      content:
        'Abre <strong>Configuración</strong>, cambia a <strong>Avanzado</strong> con el botón de la parte superior derecha del panel y elige la pestaña <strong>Proveedor</strong>. <strong>Región</strong> está justo debajo del proveedor de servicios de IA.',
      screenshot: '01-region.png',
    },
    {
      title: 'Elige una región',
      content:
        'Con el motor de Soniox, elige <strong>United States</strong>, <strong>European Union</strong> o <strong>日本</strong> (Japón); con el motor de Qwen, <strong>Singapore</strong> o <strong>北京</strong> (Pekín), por ejemplo la región que tu organización exija para sus datos. La elección se aplica a partir de la siguiente sesión.',
      screenshot: '02-region-qwen.png',
    },
    {
      title: 'Añade tu voz en la nueva región',
      content:
        'Con el motor de Soniox, las voces personalizadas pertenecen a una sola región, y cada región recuerda su propia elección de voz. Si usas <strong>Mi voz</strong>, vuelve a añadirla después de cambiar; consulta <a href="/docs/kizuna-ai/voices">Elige una voz o clona la tuya</a>. Las voces del motor de Qwen no están ligadas a una región: sus voces predefinidas y la clonación en vivo funcionan igual en Singapur y en Pekín.',
    },
  ],
  tips: {
    title: 'Consejos',
    items: [
      'La región no se puede cambiar mientras una sesión se está iniciando o está en marcha. Detén primero la sesión.',
      'Todo lo demás, incluidos los idiomas, el vocabulario y el contexto de la sesión, se mantiene igual al cambiar de región.',
      'Desde redes fuera de China continental, Pekín puede responder con una latencia notablemente mayor; si ocurre, prueba Singapur.',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿Qué región debo elegir?',
        answer:
          'Si tu organización tiene normas sobre dónde se procesan los datos, elige la región que exijan. Si no, la predeterminada sirve para todos: Estados Unidos para Soniox, Singapur para Qwen. Pekín existe para las redes de China continental.',
      },
      {
        question: '¿Dónde se procesa mi audio si nunca cambio esto?',
        answer: 'En la región predeterminada del motor: Estados Unidos para Soniox, Singapur para Qwen.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content:
      '¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. También puedes preguntar en <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Choisir où votre audio est traité',
  backLink: 'Retour au démarrage rapide KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Aperçu',
    content:
      'Chaque moteur de KizunaAI exploite un déploiement distinct par région, et votre audio est traité dans la région que vous choisissez. Le moteur Soniox propose les États-Unis, l’Union européenne et le Japon ; le moteur Qwen propose Singapour et Pékin. Les valeurs par défaut sont les États-Unis et Singapour.',
  },
  steps: [
    {
      title: 'Ouvrez le réglage de région',
      content:
        'Ouvrez les <strong>Paramètres</strong>, passez en <strong>Avancé</strong> avec le bouton en haut à droite du panneau, puis choisissez l’onglet <strong>Fournisseur</strong>. <strong>Région</strong> se trouve juste sous le fournisseur de services d’IA.',
      screenshot: '01-region.png',
    },
    {
      title: 'Choisissez une région',
      content:
        'Avec le moteur Soniox, choisissez <strong>United States</strong>, <strong>European Union</strong> ou <strong>日本</strong> (Japon) ; avec le moteur Qwen, <strong>Singapore</strong> ou <strong>北京</strong> (Pékin), par exemple la région que votre organisation exige pour ses données. Le choix s’applique à partir de la session suivante.',
      screenshot: '02-region-qwen.png',
    },
    {
      title: 'Ajoutez votre voix dans la nouvelle région',
      content:
        'Avec le moteur Soniox, une voix personnalisée appartient à une seule région, et chaque région mémorise son propre choix de voix. Si vous utilisez <strong>Ma voix</strong>, ajoutez-la de nouveau après le changement ; voir <a href="/docs/kizuna-ai/voices">Choisir une voix ou cloner la vôtre</a>. Les voix du moteur Qwen ne sont pas liées à une région : ses voix prédéfinies et son clonage en direct fonctionnent de la même façon à Singapour et à Pékin.',
    },
  ],
  tips: {
    title: 'Conseils',
    items: [
      'La région ne peut pas être modifiée pendant le démarrage ou l’exécution d’une session. Arrêtez d’abord la session.',
      'Tout le reste, y compris les langues, le vocabulaire et le contexte de la session, reste inchangé quand vous changez de région.',
      'Depuis un réseau hors de Chine continentale, Pékin peut avoir une latence nettement plus élevée ; dans ce cas, essayez Singapour.',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Quelle région choisir ?',
        answer:
          'Si votre organisation a des règles sur le lieu de traitement des données, choisissez la région qu’elle exige. Sinon, la région par défaut convient à tout le monde : les États-Unis pour Soniox, Singapour pour Qwen. Pékin est là pour les réseaux de Chine continentale.',
      },
      {
        question: 'Où mon audio est-il traité si je ne change jamais ce réglage ?',
        answer: 'Dans la région par défaut du moteur : les États-Unis pour Soniox, Singapour pour Qwen.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content:
      'Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'اختر مكان معالجة صوتك',
  backLink: 'العودة إلى البدء السريع مع KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'نظرة عامة',
    content:
      'يشغّل كل محرك من محركَي KizunaAI نشرًا منفصلًا لكل منطقة، ويُعالَج صوتك في المنطقة التي تختارها. يوفّر محرك Soniox الولايات المتحدة والاتحاد الأوروبي واليابان؛ ويوفّر محرك Qwen سنغافورة وبكين. المنطقتان الافتراضيتان هما الولايات المتحدة وسنغافورة.',
  },
  steps: [
    {
      title: 'افتح إعداد المنطقة',
      content:
        'افتح <strong>الإعدادات</strong>، وانتقل إلى <strong>متقدم</strong> بالزر الموجود أعلى يمين اللوحة، ثم اختر علامة التبويب <strong>المزود</strong>. يوجد إعداد <strong>المنطقة</strong> أسفل مزود خدمة الذكاء الاصطناعي مباشرة.',
      screenshot: '01-region.png',
    },
    {
      title: 'اختر منطقة',
      content:
        'مع محرك Soniox، اختر <strong>United States</strong> أو <strong>European Union</strong> أو <strong>日本</strong> (اليابان)؛ ومع محرك Qwen، اختر <strong>Singapore</strong> أو <strong>北京</strong> (بكين)، مثل المنطقة التي تشترطها مؤسستك لبياناتها. يُطبَّق الاختيار بدءًا من الجلسة التالية.',
      screenshot: '02-region-qwen.png',
    },
    {
      title: 'أضف صوتك في المنطقة الجديدة',
      content:
        'مع محرك Soniox، ينتمي الصوت المخصص إلى منطقة واحدة، وتتذكر كل منطقة اختيارها الخاص للصوت. إذا كنت تستخدم <strong>صوتي</strong>، فأضفه مرة أخرى بعد التبديل؛ راجع <a href="/docs/kizuna-ai/voices">اختر صوتًا أو استنسخ صوتك</a>. أما أصوات محرك Qwen فلا ترتبط بمنطقة: أصواته الجاهزة واستنساخه المباشر يعملان بالطريقة نفسها في سنغافورة وبكين.',
    },
  ],
  tips: {
    title: 'نصائح',
    items: [
      'لا يمكن تغيير المنطقة أثناء بدء الجلسة أو تشغيلها. أوقف الجلسة أولًا.',
      'يبقى كل شيء آخر كما هو عند تبديل المنطقة، بما في ذلك اللغات والمفردات وخلفية الجلسة.',
      'من الشبكات خارج البر الصيني الرئيسي، قد يكون زمن استجابة بكين أعلى بشكل ملحوظ؛ في هذه الحالة جرّب سنغافورة.',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'أي منطقة يجب أن أختار؟',
        answer:
          'إذا كانت لدى مؤسستك قواعد بشأن مكان معالجة البيانات، فاختر المنطقة التي تشترطها. وإلا فإن المنطقة الافتراضية تناسب الجميع: الولايات المتحدة مع Soniox، وسنغافورة مع Qwen. وبكين متاحة للشبكات داخل البر الصيني الرئيسي.',
      },
      {
        question: 'أين يُعالَج صوتي إذا لم أغيّر هذا الإعداد أبدًا؟',
        answer: 'في المنطقة الافتراضية للمحرك: الولايات المتحدة مع Soniox، وسنغافورة مع Qwen.',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content:
      'هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Escolha onde o seu áudio é processado',
  backLink: 'Voltar ao início rápido com KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Visão geral',
    content:
      'Cada mecanismo da KizunaAI mantém uma implantação separada por região, e o seu áudio é processado na região que você escolher. O mecanismo Soniox oferece os Estados Unidos, a União Europeia e o Japão; o mecanismo Qwen oferece Singapura e Pequim. Os padrões são os Estados Unidos e Singapura.',
  },
  steps: [
    {
      title: 'Abra a configuração de região',
      content:
        'Abra <strong>Configurações</strong>, mude para <strong>Avançado</strong> com o botão no canto superior direito do painel e escolha a aba <strong>Provedor</strong>. <strong>Região</strong> fica logo abaixo do provedor de serviços de IA.',
      screenshot: '01-region.png',
    },
    {
      title: 'Escolha uma região',
      content:
        'Com o mecanismo Soniox, escolha <strong>United States</strong>, <strong>European Union</strong> ou <strong>日本</strong> (Japão); com o mecanismo Qwen, <strong>Singapore</strong> ou <strong>北京</strong> (Pequim), por exemplo a região que a sua organização exige para os dados dela. A escolha vale a partir da próxima sessão.',
      screenshot: '02-region-qwen.png',
    },
    {
      title: 'Adicione a sua voz na nova região',
      content:
        'Com o mecanismo Soniox, as vozes personalizadas pertencem a uma região, e cada região guarda a sua própria escolha de voz. Se você usa <strong>Minha voz</strong>, adicione-a de novo depois de trocar; veja <a href="/docs/kizuna-ai/voices">Escolha uma voz ou clone a sua</a>. As vozes do mecanismo Qwen não dependem de região: as predefinidas e a clonagem ao vivo funcionam do mesmo jeito em Singapura e em Pequim.',
    },
  ],
  tips: {
    title: 'Dicas',
    items: [
      'Não é possível mudar a região enquanto uma sessão está iniciando ou em andamento. Pare a sessão primeiro.',
      'Todo o resto, incluindo idiomas, vocabulário e o contexto da sessão, continua igual quando você troca de região.',
      'A partir de redes fora da China continental, Pequim pode responder com latência bem maior; se isso acontecer, experimente Singapura.',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'Qual região devo escolher?',
        answer:
          'Se a sua organização tem regras sobre onde os dados são processados, escolha a região que ela exige. Caso contrário, o padrão serve para todos: os Estados Unidos para a Soniox, Singapura para a Qwen. Pequim existe para as redes da China continental.',
      },
      {
        question: 'Onde o meu áudio é processado se eu nunca mudar isso?',
        answer: 'Na região padrão do mecanismo: os Estados Unidos para a Soniox, Singapura para a Qwen.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Você também pode perguntar no <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Выберите, где обрабатывается ваш звук',
  backLink: 'Назад к быстрому старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Обзор',
    content:
      'Каждый движок KizunaAI работает отдельным развёртыванием в каждом регионе, и ваш звук обрабатывается в выбранном вами регионе. Движок Soniox предлагает США, Европейский союз и Японию, движок Qwen — Сингапур и Пекин. По умолчанию это США и Сингапур соответственно.',
  },
  steps: [
    {
      title: 'Откройте настройку региона',
      content:
        'Откройте <strong>Настройки</strong>, переключитесь в режим <strong>Расширенный</strong> кнопкой в правом верхнем углу панели и выберите вкладку <strong>Поставщик</strong>. Поле <strong>Регион</strong> находится сразу под поставщиком услуг ИИ.',
      screenshot: '01-region.png',
    },
    {
      title: 'Выберите регион',
      content:
        'С движком Soniox выберите <strong>United States</strong>, <strong>European Union</strong> или <strong>日本</strong> (Япония); с движком Qwen — <strong>Singapore</strong> или <strong>北京</strong> (Пекин), например, регион, которого требует ваша организация для своих данных. Выбор действует со следующего сеанса.',
      screenshot: '02-region-qwen.png',
    },
    {
      title: 'Добавьте голос в новом регионе',
      content:
        'С движком Soniox пользовательские голоса привязаны к одному региону, и каждый регион запоминает свой выбор голоса. Если вы используете <strong>Мой голос</strong>, добавьте его заново после переключения; см. <a href="/docs/kizuna-ai/voices">Выберите голос или клонируйте свой</a>. Голоса движка Qwen к региону не привязаны: его готовые голоса и клонирование вживую одинаково работают в Сингапуре и Пекине.',
    },
  ],
  tips: {
    title: 'Советы',
    items: [
      'Регион нельзя изменить, пока сеанс запускается или идёт. Сначала остановите сеанс.',
      'Всё остальное, включая языки, словарь и контекст сеанса, при смене региона остаётся прежним.',
      'Из сетей за пределами материкового Китая задержка до Пекина может быть заметно выше; в таком случае попробуйте Сингапур.',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Какой регион выбрать?',
        answer:
          'Если в вашей организации есть правила о том, где обрабатываются данные, выберите требуемый ими регион. В остальных случаях подойдёт регион по умолчанию: США для Soniox, Сингапур для Qwen. Пекин предназначен для сетей в материковом Китае.',
      },
      {
        question: 'Где обрабатывается мой звук, если я ничего не менял?',
        answer: 'В регионе по умолчанию для вашего движка: в США для Soniox, в Сингапуре для Qwen.',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Также можно задать вопрос в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'Festlegen, wo Ihr Audio verarbeitet wird',
  backLink: 'Zurück zum KizunaAI-Schnellstart',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Übersicht',
    content:
      'Jede KizunaAI-Engine betreibt pro Region eine eigene Bereitstellung, und Ihr Audio wird in der Region verarbeitet, die Sie wählen. Die Soniox-Engine bietet die USA, die Europäische Union und Japan; die Qwen-Engine bietet Singapur und Peking. Standard sind die USA und Singapur.',
  },
  steps: [
    {
      title: 'Regionseinstellung öffnen',
      content:
        'Öffnen Sie die <strong>Einstellungen</strong>, wechseln Sie mit der Schaltfläche oben rechts im Bereich zu <strong>Erweitert</strong> und wählen Sie den Tab <strong>Anbieter</strong>. <strong>Region</strong> befindet sich direkt unter dem KI-Dienstanbieter.',
      screenshot: '01-region.png',
    },
    {
      title: 'Region wählen',
      content:
        'Wählen Sie mit der Soniox-Engine <strong>United States</strong>, <strong>European Union</strong> oder <strong>日本</strong> (Japan), mit der Qwen-Engine <strong>Singapore</strong> oder <strong>北京</strong> (Peking), zum Beispiel die Region, die Ihre Organisation für ihre Daten vorschreibt. Die Auswahl gilt ab der nächsten Sitzung.',
      screenshot: '02-region-qwen.png',
    },
    {
      title: 'Stimme in der neuen Region hinzufügen',
      content:
        'Mit der Soniox-Engine gehören benutzerdefinierte Stimmen zu einer Region, und jede Region merkt sich ihre eigene Stimmauswahl. Wenn Sie <strong>Meine Stimme</strong> verwenden, fügen Sie sie nach dem Wechsel erneut hinzu; siehe <a href="/docs/kizuna-ai/voices">Stimme wählen oder eigene Stimme klonen</a>. Die Stimmen der Qwen-Engine sind nicht an eine Region gebunden: Ihre vordefinierten Stimmen und das Live-Klonen funktionieren in Singapur und Peking gleich.',
    },
  ],
  tips: {
    title: 'Tipps',
    items: [
      'Die Region lässt sich nicht ändern, während eine Sitzung startet oder läuft. Beenden Sie zuerst die Sitzung.',
      'Alles andere, einschließlich Sprachen, Vokabular und Sitzungshintergrund, bleibt beim Regionswechsel gleich.',
      'Aus Netzen außerhalb Festlandchinas kann Peking eine deutlich höhere Latenz haben; versuchen Sie dann Singapur.',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Welche Region soll ich wählen?',
        answer:
          'Wenn Ihre Organisation Regeln dazu hat, wo Daten verarbeitet werden, wählen Sie die vorgeschriebene Region. Ansonsten passt der Standard für alle: die USA für Soniox, Singapur für Qwen. Peking ist für Netze in Festlandchina gedacht.',
      },
      {
        question: 'Wo wird mein Audio verarbeitet, wenn ich hier nie etwas ändere?',
        answer: 'In der Standardregion der Engine: den USA bei Soniox, Singapur bei Qwen.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Sie können auch in den <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> fragen.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Виберіть, де обробляється ваш звук',
  backLink: 'Назад до швидкого старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Огляд',
    content:
      'Кожен рушій KizunaAI працює окремим розгортанням у кожному регіоні, і ваш звук обробляється в регіоні, який ви виберете. Рушій Soniox пропонує США, Європейський Союз і Японію; рушій Qwen — Сінгапур і Пекін. За замовчуванням це США та Сінгапур.',
  },
  steps: [
    {
      title: 'Відкрийте налаштування регіону',
      content:
        'Відкрийте <strong>Налаштування</strong>, перейдіть у режим <strong>Розширений</strong> кнопкою в правому верхньому куті панелі та виберіть вкладку <strong>Постачальник</strong>. Поле <strong>Регіон</strong> розташоване одразу під постачальником послуг ШІ.',
      screenshot: '01-region.png',
    },
    {
      title: 'Виберіть регіон',
      content:
        'З рушієм Soniox виберіть <strong>United States</strong>, <strong>European Union</strong> або <strong>日本</strong> (Японія), а з рушієм Qwen — <strong>Singapore</strong> або <strong>北京</strong> (Пекін) — наприклад, регіон, якого ваша організація вимагає для своїх даних. Вибір діє з наступного сеансу.',
      screenshot: '02-region-qwen.png',
    },
    {
      title: 'Додайте голос у новому регіоні',
      content:
        'З рушієм Soniox користувацькі голоси прив’язані до одного регіону, і кожен регіон запам’ятовує свій вибір голосу. Якщо ви використовуєте <strong>Мій голос</strong>, додайте його знову після перемикання; див. <a href="/docs/kizuna-ai/voices">Виберіть голос або клонуйте свій</a>. Голоси рушія Qwen не прив’язані до регіону: його готові голоси та клонування наживо однаково працюють у Сінгапурі й Пекіні.',
    },
  ],
  tips: {
    title: 'Поради',
    items: [
      'Регіон не можна змінити, поки сеанс запускається або триває. Спершу зупиніть сеанс.',
      'Усе інше, зокрема мови, словник і тло сеансу, під час зміни регіону залишається без змін.',
      'З мереж за межами материкового Китаю Пекін може відповідати з помітно вищою затримкою; якщо так, спробуйте Сінгапур.',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Який регіон вибрати?',
        answer:
          'Якщо у вашій організації є правила щодо того, де обробляються дані, виберіть регіон, якого вони вимагають. В інших випадках підійде регіон за замовчуванням: США для Soniox, Сінгапур для Qwen. Пекін призначений для мереж у материковому Китаї.',
      },
      {
        question: 'Де обробляється мій звук, якщо я нічого не змінював?',
        answer: 'У регіоні рушія за замовчуванням: у США для Soniox, у Сінгапурі для Qwen.',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Також можна запитати в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Scegli dove viene elaborato il tuo audio',
  backLink: 'Torna alla guida rapida a KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Panoramica',
    content:
      'Ogni motore di KizunaAI gestisce un’installazione separata per ciascuna regione, e il tuo audio viene elaborato nella regione che scegli. Il motore Soniox offre Stati Uniti, Unione Europea e Giappone; il motore Qwen offre Singapore e Pechino. Le impostazioni predefinite sono Stati Uniti e Singapore.',
  },
  steps: [
    {
      title: 'Apri l’impostazione della regione',
      content:
        'Apri <strong>Impostazioni</strong>, passa ad <strong>Avanzato</strong> con il pulsante in alto a destra del pannello e scegli la scheda <strong>Fornitore</strong>. <strong>Regione</strong> si trova subito sotto il fornitore di servizi AI.',
      screenshot: '01-region.png',
    },
    {
      title: 'Scegli una regione',
      content:
        'Con il motore Soniox scegli <strong>United States</strong>, <strong>European Union</strong> o <strong>日本</strong> (Giappone); con il motore Qwen, <strong>Singapore</strong> o <strong>北京</strong> (Pechino), per esempio la regione che la tua organizzazione richiede per i propri dati. La scelta vale dalla sessione successiva.',
      screenshot: '02-region-qwen.png',
    },
    {
      title: 'Aggiungi la tua voce nella nuova regione',
      content:
        'Con il motore Soniox, le voci personalizzate appartengono a una sola regione, e ogni regione ricorda la propria scelta di voce. Se usi <strong>La mia voce</strong>, aggiungila di nuovo dopo il cambio; vedi <a href="/docs/kizuna-ai/voices">Scegli una voce o clona la tua</a>. Le voci del motore Qwen non sono legate a una regione: i preset e la clonazione dal vivo funzionano allo stesso modo a Singapore e a Pechino.',
    },
  ],
  tips: {
    title: 'Consigli',
    items: [
      'La regione non si può cambiare mentre una sessione si sta avviando o è attiva. Interrompi prima la sessione.',
      'Tutto il resto, comprese lingue, vocabolario e contesto della sessione, resta invariato quando cambi regione.',
      'Dalle reti fuori dalla Cina continentale, Pechino può avere una latenza decisamente più alta; in tal caso prova Singapore.',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'Quale regione dovrei scegliere?',
        answer:
          'Se la tua organizzazione ha regole su dove vengono elaborati i dati, scegli la regione che richiede. Altrimenti l’impostazione predefinita va bene per tutti: gli Stati Uniti per Soniox, Singapore per Qwen. Pechino c’è per le reti nella Cina continentale.',
      },
      {
        question: 'Dove viene elaborato il mio audio se non cambio mai questa impostazione?',
        answer: 'Nella regione predefinita del motore: gli Stati Uniti per Soniox, Singapore per Qwen.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Puoi anche chiedere su <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function KizunaRegionGuide() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/kizuna-ai-region" />;
}
